import { ClassData } from "../../../common/dtos/classes.type";
import {
  filterCampsByAge,
  filterCampsByGender,
  filterCampsBySession,
  filterCampsByMonth,
} from "../../templates/camps/filter";
import { setEventView } from "../views/event.view";

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

interface EventFilterState {
  allData: ClassData[];
  listSelector: string;
  useDescription: boolean;
  containerId: string;
}

const filterStates: Record<string, EventFilterState> = {};

/**
 * Build and inject the filter bar HTML before the event list,
 * populate dynamic options from data, and wire up interactions.
 */
export function initEventFilters(
  containerId: string,
  allData: ClassData[],
  listSelector: string,
  useDescription: boolean = false
): void {
  const $container = $(`#${containerId}`);
  if (!$container.length || !allData.length) return;

  filterStates[containerId] = { allData, listSelector, useDescription, containerId };

  const filterBarHtml = buildFilterBarHtml();
  const $list = $(listSelector);
  if ($list.length) {
    $list.before(filterBarHtml);
  }

  const $bar = $container.find(".events-filter-bar");
  if (!$bar.length) return;

  populateOptions($bar, allData);
  bindFilterEvents($bar, containerId);
}

function buildFilterBarHtml(): string {
  return `
    <div class="events-filter-bar">
      <div class="events-filters-wrap">
        <button class="events-quick-filter" style="display:none;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
          </svg>
          <span class="events-quick-label"></span>
        </button>
        <div class="events-filter-pill" data-filter="session">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/>
          </svg>
          <span class="events-pill-text"><span class="events-pill-label">Session:</span> <span class="events-pill-value">All</span></span>
          <svg class="events-pill-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="events-filter-dropdown"><ul><li value="" class="selected">All Sessions</li></ul></div>
        </div>
        <div class="events-filter-pill" data-filter="month">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span class="events-pill-text"><span class="events-pill-label">Month:</span> <span class="events-pill-value">All</span></span>
          <svg class="events-pill-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="events-filter-dropdown"><ul><li value="" class="selected">All Months</li></ul></div>
        </div>
        <div class="events-filter-pill" data-filter="age">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4"/><path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
          </svg>
          <span class="events-pill-text"><span class="events-pill-label">Age:</span> <span class="events-pill-value">All</span></span>
          <svg class="events-pill-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="events-filter-dropdown">
            <ul>
              <li value="" class="selected">All Ages</li>
              ${Array.from({ length: 18 }, (_, i) => i + 1)
                .map((age) => `<li value="${age}">${age} ${age === 1 ? "year" : "years"}</li>`)
                .join("")}
            </ul>
          </div>
        </div>
        <button class="events-clear-filters" style="display:none;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Clear filters
        </button>
      </div>
    </div>`;
}

function populateOptions($bar: JQuery, data: ClassData[]): void {
  // Sessions
  const sessionSet: Record<string, boolean> = {};
  const sessions: string[] = [];
  data.forEach((item: any) => {
    if (item.session && !sessionSet[item.session]) {
      sessionSet[item.session] = true;
      sessions.push(item.session);
    }
  });
  const $sessionUl = $bar.find('.events-filter-pill[data-filter="session"] ul');
  sessions.forEach((s) => $sessionUl.append($("<li>").attr("value", s).text(s)));

  // Hide session pill if only one session
  if (sessions.length <= 1) {
    $bar.find('.events-filter-pill[data-filter="session"]').hide();
  }

  // Summer quick filter
  const summerSession = sessions.find((s) => s.toLowerCase().includes("summer"));
  if (summerSession) {
    const $quickBtn = $bar.find(".events-quick-filter");
    $quickBtn.data("session-value", summerSession);
    $quickBtn.find(".events-quick-label").text(summerSession);
    $quickBtn.show();
  }

  // Months
  const monthSet: Record<string, boolean> = {};
  const months: { slug: string; label: string }[] = [];
  data.forEach((item: any) => {
    if (!item.start_date) return;
    const parts = item.start_date.split("-");
    if (parts.length < 2) return;
    const year = parseInt(parts[0], 10);
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return;
    const slug = MONTHS_FULL[monthIndex].toLowerCase() + "-" + year;
    if (monthSet[slug]) return;
    monthSet[slug] = true;
    months.push({ slug, label: MONTHS_FULL[monthIndex] + " " + year });
  });
  months.sort((a, b) => {
    const [aM, aY] = a.slug.split("-");
    const [bM, bY] = b.slug.split("-");
    if (aY !== bY) return parseInt(aY) - parseInt(bY);
    return MONTHS_FULL.findIndex((m) => m.toLowerCase() === aM) -
      MONTHS_FULL.findIndex((m) => m.toLowerCase() === bM);
  });
  const $monthUl = $bar.find('.events-filter-pill[data-filter="month"] ul');
  months.forEach((m) => $monthUl.append($("<li>").attr("value", m.slug).text(m.label)));
}

function bindFilterEvents($bar: JQuery, containerId: string): void {
  // Pill click — toggle dropdown
  $bar.on("click", ".events-filter-pill", function (e) {
    // Don't toggle if click came from a dropdown item
    if ($(e.target).closest(".events-filter-dropdown").length) return;
    e.stopPropagation();
    const $pill = $(this);
    if ($pill.hasClass("active")) {
      $pill.removeClass("active");
      return;
    }
    $bar.find(".events-filter-pill").removeClass("active");
    $pill.addClass("active");
  });

  // Close on outside click
  $(document).on("click", function () {
    $bar.find(".events-filter-pill").removeClass("active");
  });

  // Dropdown item click
  $bar.on("click", ".events-filter-dropdown li", function (e) {
    e.stopPropagation();
    const $li = $(this);
    const value = $li.attr("value") || "";
    const text = $li.text();
    const $pill = $li.closest(".events-filter-pill");
    const filterName = $pill.data("filter") as string;
    const $ul = $li.closest("ul");

    $ul.find("li").removeClass("selected");
    $li.addClass("selected");
    $pill.find(".events-pill-value").text(value ? text : "All");
    $pill.toggleClass("has-value", !!value);
    $pill.removeClass("active");

    // Sync quick filter state if session changed
    if (filterName === "session") {
      updateQuickFilterState($bar);
    }

    updateClearButton($bar);
    applyAndRender(containerId);
  });

  // Quick filter click
  $bar.on("click", ".events-quick-filter", function (e) {
    e.stopPropagation();
    const $btn = $(this);
    const sessionValue = $btn.data("session-value") as string;
    if (!sessionValue) return;

    if ($btn.hasClass("active")) {
      $btn.removeClass("active");
      updatePillDisplay($bar, "session", "", "All");
    } else {
      $btn.addClass("active");
      updatePillDisplay($bar, "session", sessionValue, sessionValue);
    }

    updateClearButton($bar);
    applyAndRender(containerId);
  });

  // Clear all filters
  $bar.on("click", ".events-clear-filters", function (e) {
    e.stopPropagation();
    clearAllFilters($bar);
    applyAndRender(containerId);
  });
}

function updatePillDisplay($bar: JQuery, filterName: string, value: string, displayText: string): void {
  const $pill = $bar.find(`.events-filter-pill[data-filter="${filterName}"]`);
  const $ul = $pill.find(".events-filter-dropdown ul");

  $pill.find(".events-pill-value").text(displayText);
  $ul.find("li").removeClass("selected");

  if (value) {
    $pill.addClass("has-value");
    $ul.find(`li[value="${value}"]`).addClass("selected");
  } else {
    $pill.removeClass("has-value");
    $ul.find('li[value=""]').addClass("selected");
  }
}

function updateQuickFilterState($bar: JQuery): void {
  const $quickBtn = $bar.find(".events-quick-filter");
  if (!$quickBtn.length) return;

  const quickSession = ($quickBtn.data("session-value") || "") as string;
  const $sessionPill = $bar.find('.events-filter-pill[data-filter="session"]');
  const selectedSession = $sessionPill.find("li.selected").attr("value") || "";

  $quickBtn.toggleClass("active", !!selectedSession && selectedSession === quickSession);
}

function updateClearButton($bar: JQuery): void {
  const hasActive = $bar.find(".events-filter-pill.has-value").length > 0;
  $bar.find(".events-clear-filters").toggle(hasActive);
}

function clearAllFilters($bar: JQuery): void {
  $bar.find(".events-filter-pill").each(function () {
    const $pill = $(this);
    const $ul = $pill.find("ul");
    $ul.find("li").removeClass("selected");
    $ul.find('li[value=""]').addClass("selected");
    $pill.find(".events-pill-value").text("All");
    $pill.removeClass("has-value");
  });
  $bar.find(".events-quick-filter").removeClass("active");
  $bar.find(".events-clear-filters").hide();
}

function getActiveFilters($container: JQuery): { session: string; month: string; age: string; gender: string } {
  const getVal = (filterName: string): string => {
    const $selected = $container.find(`.events-filter-pill[data-filter="${filterName}"] li.selected`);
    return $selected.attr("value") || "";
  };
  return {
    session: getVal("session"),
    month: getVal("month"),
    age: getVal("age"),
    gender: getVal("gender"),
  };
}

function applyAndRender(containerId: string): void {
  const state = filterStates[containerId];
  if (!state) return;

  const $container = $(`#${containerId}`);
  const filters = getActiveFilters($container);

  let filtered = state.allData;
  if (filters.session) filtered = filterCampsBySession(filtered, filters.session);
  if (filters.month) filtered = filterCampsByMonth(filtered, filters.month);
  if (filters.age) filtered = filterCampsByAge(filtered, filters.age);
  if (filters.gender) filtered = filterCampsByGender(filtered, filters.gender);

  const $list = $(state.listSelector);
  $list.empty();

  if (!filtered.length) {
    const hasAnyFilter = filters.session || filters.month || filters.age || filters.gender;
    if (hasAnyFilter) {
      $list.append(`
        <div class="no-filter-results">
          <p>No camps match your current filters.</p>
          <button class="reset-filters-btn">Reset filters</button>
        </div>
      `);
      $list.find(".reset-filters-btn").on("click", function () {
        clearAllFilters($container.find(".events-filter-bar"));
        applyAndRender(containerId);
      });
      return;
    }
  }

  setEventView(filtered, state.listSelector, state.useDescription);
}
